///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { isDefined } from '@siklu/sui-lib/utils';
import { catchError } from 'rxjs/operators';
import { forkJoin, from, map, Observable, of } from 'rxjs';

import { GtthStepGroupEnum } from '../../generated/shared-types.model';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { NodeFeature } from '../logical-topology/entities/node/node-feature';
import { LinkFeature } from '../logical-topology/entities/link/link-feature';
import { IP_OPTIONAL_VALUE, IP_ZERO_VALUE } from '../../shared/directives/validation-custom/ip-address.validator';
import { SearchResultStation, SearchResultStations } from './search-station.model';

export interface GeocodingResults {
    latLng: google.maps.LatLng;
    address: string;
}

export interface SearchResults {
    stations: SearchResultStations;
    locations: google.maps.places.PlacePrediction[];
}

@Injectable()
export class ActionsToolbarSearchService {
    private searchSessionToken: google.maps.places.AutocompleteSessionToken;
    private allStationsList: SearchResultStation[] = [];
    private readonly stationAssignedNamesMap: Map<number, string[]> = new Map<number, string[]>();
    private readonly stationIPsMap: Map<number, string[]> = new Map<number, string[]>();

    constructor(private readonly historyService: GtthHistoryService) {}

    public generateAllStationsList(): void {
        this.searchSessionToken = new google.maps.places.AutocompleteSessionToken();

        this.clearAllStationsList();

        const stationsIterator: IterableIterator<NodeFeature> = this.historyService.logicalTopology.getStationsIterator();
        for (const station of stationsIterator) {
            const stationId = station.getId();
            const stationAssignedNamesSet = new Set<string>();
            const stationIPsSet = new Set<string>();

            // Filter links depend on step, design and link state
            const stationLinks = this.historyService.logicalTopology
                .getAllStationLinks(station.getId())
                .filter((link: LinkFeature) => this.isLinkShouldBeProcessed(link));

            for (const link of stationLinks) {
                const isStationInitiator = stationId === link.getFromId();
                const assignedName = isStationInitiator ? link.getAssignedName0() : link.getAssignedName1();
                const ipAddress = isStationInitiator ? link.getNode0IPAddress() : link.getNode1IPAddress();

                if (assignedName) {
                    stationAssignedNamesSet.add(assignedName);
                }

                if (this.isIpAddressShouldBeProcessed(ipAddress)) {
                    stationIPsSet.add(ipAddress);
                }
            }

            this.stationAssignedNamesMap.set(stationId, Array.from(stationAssignedNamesSet));
            this.stationIPsMap.set(stationId, Array.from(stationIPsSet));
            this.allStationsList.push({
                id: stationId,
                name: station.getName(),
                latLng: new google.maps.LatLng({
                    lat: station.getPosition().latitude,
                    lng: station.getPosition().longitude
                })
            });
        }
    }

    private isLinkShouldBeProcessed(link: LinkFeature): boolean {
        return (
            link.isUserForced() ||
            ((this.historyService.selectedStepNumber === GtthStepGroupEnum.PLAN_AND_OPTIMISE ||
                this.historyService.selectedStepNumber === GtthStepGroupEnum.BOM) &&
                this.historyService.logicalTopology.isShowPlanningMode)
        );
    }

    private isIpAddressShouldBeProcessed(ipAddress: string): boolean {
        return ipAddress && ipAddress !== IP_ZERO_VALUE && ipAddress !== IP_OPTIONAL_VALUE;
    }

    public clearAllStationsList(): void {
        this.allStationsList = [];
        this.stationIPsMap.clear();
        this.stationAssignedNamesMap.clear();
    }

    public geocode(address: string): Observable<GeocodingResults> {
        return new Observable((observer) => {
            this.historyService.ngZone.runOutsideAngular(() => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ address }, (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK && results?.length > 0) {
                        observer.next({
                            latLng: results[0].geometry.location,
                            address
                        });
                    }
                    observer.complete();
                });
            });
        });
    }

    public getSearchResults(searchString: string): Observable<SearchResults> {
        return forkJoin({
            stations: this.searchStations(searchString),
            locations: this.searchLocations(searchString)
        });
    }

    private searchStations(searchString: string): Observable<SearchResultStations> {
        const searchResult: SearchResultStations = {
            stationsByName: [],
            stationsByAssignedName: [],
            stationsByIPAddress: []
        };

        const lowerSearchString = searchString.toLowerCase();
        const isSearchByIpAddress = /^(\d|\.|\*)+$/.test(searchString);

        for (const station of this.allStationsList) {
            try {
                this.searchStationByName(station, lowerSearchString, searchResult);
                this.searchStationByAssignedNames(station, lowerSearchString, searchResult);
                if (isSearchByIpAddress) {
                    this.searchStationByIpAddress(station, searchString, searchResult);
                }
            } catch (e) {
                // Skip this station on error
            }
        }

        this.sortSearchResults(searchResult);

        return of(searchResult);
    }

    private searchStationByName(station: SearchResultStation, lowerSearchString: string, searchResult: SearchResultStations): void {
        if (station.name.toLowerCase().includes(lowerSearchString)) {
            searchResult.stationsByName.push(station);
        }
    }

    private searchStationByAssignedNames(
        station: SearchResultStation,
        lowerSearchString: string,
        searchResult: SearchResultStations
    ): void {
        const assignedNames = this.stationAssignedNamesMap.get(station.id);
        if (!assignedNames) {
            return;
        }

        for (const assignedName of assignedNames) {
            if (assignedName.toLowerCase().includes(lowerSearchString)) {
                searchResult.stationsByAssignedName.push({
                    ...station,
                    deviceAN: assignedName
                });
            }
        }
    }

    private searchStationByIpAddress(station: SearchResultStation, searchString: string, searchResult: SearchResultStations): void {
        const stationIPs = this.stationIPsMap.get(station.id);
        if (!stationIPs) {
            return;
        }

        for (const ip of stationIPs) {
            if (ip.includes(searchString)) {
                searchResult.stationsByIPAddress.push({
                    ...station,
                    deviceIP: ip
                });
            }
        }
    }

    private sortSearchResults(searchResult: SearchResultStations): void {
        searchResult.stationsByName.sort((a, b) => a?.name.localeCompare(b?.name));
        searchResult.stationsByAssignedName.sort((a, b) => a?.deviceAN?.localeCompare(b?.deviceAN));
        searchResult.stationsByIPAddress.sort((a, b) => a?.deviceIP?.localeCompare(b?.deviceIP));
    }

    private searchLocations(geocodingPlace: string): Observable<google.maps.places.PlacePrediction[]> {
        let requestString = geocodingPlace;
        if (geocodingPlace.indexOf('*') !== -1) {
            requestString = geocodingPlace.replace(/\*/g, '');
        }

        return from(
            google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: requestString,
                sessionToken: this.searchSessionToken
            })
        ).pipe(
            map((result: { suggestions: google.maps.places.AutocompleteSuggestion[] }) => {
                const predictions = result?.suggestions
                    ?.map((suggestion) => suggestion.placePrediction)
                    ?.filter((prediction) => isDefined(prediction));

                return predictions || [];
            }),
            catchError(() => of([]))
        );
    }
}
